// Which deployment is this browser looking at?
//
// The SPA is one build served from both hostnames, so the answer comes from the
// URL at runtime — never from a build-time env var. It uses the SAME resolution
// rule as the server (@ow/shared tenant.ts), so the country the page is labelled
// with and the country the API returns can't drift apart.

import {
  DEFAULT_TENANT,
  TENANT_HEADER,
  type TenantId,
  type TenantProfile,
  demoSegmentLabel,
  resolveTenant,
  tenantProfile,
} from "@ow/shared";

/** Resolved once at module load: a tenant switch means a different hostname,
 *  which means a full document load anyway.
 *
 *  Guarded for the non-browser case — the unit tests import components (and so
 *  this module) under Node, where there is no `window`. */
export const TENANT: TenantId =
  typeof window === "undefined"
    ? DEFAULT_TENANT
    : resolveTenant({
        host: window.location.hostname,
        override: new URLSearchParams(window.location.search).get("tenant"),
      });

/** The country of focus and everything that names it — provinces, gateways,
 *  currency, capital. Components read this instead of hardcoding "Saudi". */
export const TENANT_PROFILE: TenantProfile = tenantProfile(TENANT);

/**
 * Header sent on every dashboard read so the API resolves the same tenant the
 * page did.
 *
 * On the real hostnames the server would work this out from the Host header
 * regardless; this exists so a `?tenant=` override on the page also applies to
 * its data, which is what makes the demo testable before its DNS record exists.
 */
export function tenantHeaders(): Record<string, string> {
  return { [TENANT_HEADER]: TENANT };
}

// --- Copy shorthands ------------------------------------------------------
//
// The section components name the focus country constantly — in headings, map
// aria-labels, source lines and axis titles. Reaching through
// `TENANT_PROFILE.shortName` at every one of those sites reads badly inside
// JSX, so the fields the copy actually uses are re-exported under the names the
// prose wants. These are the ONLY spellings of the country that should appear
// in a component; a literal "Saudi" in user-facing text is a bug on the demo.

/** "Saudi" · "Wonderland" — the short form most labels use. */
export const COUNTRY = TENANT_PROFILE.shortName;
/** "Saudi Arabia" · "Wonderland" — the formal name, for source lines and captions. */
export const COUNTRY_FULL = TENANT_PROFILE.name;
/** "Saudi" · "Wonderland" — the adjective ("Saudi tourism", "Wonderland tourism"). */
export const COUNTRY_ADJ = TENANT_PROFILE.adjective;
/** "the Kingdom" · "Wonderland" — the stand-in the copy uses for variety. The
 *  demo has none: the client asked that "the Realm" never appear, so there it
 *  is the country's own name. */
export const THE_COUNTRY = TENANT_PROFILE.theCountry;
/** Capitalised for sentence-initial use: "The Kingdom" · "Wonderland". */
export const THE_COUNTRY_CAP = TENANT_PROFILE.theCountry.replace(/^the\b/, "The");
/** "SAR" · "WLC" — the currency spend figures are denominated in. */
export const CURRENCY = TENANT_PROFILE.currency.code;
/** "Riyadh" · "Port Célestin". */
export const CAPITAL = TENANT_PROFILE.capital;
/** "Vision 2030" · "Wonderland Tourism Strategy 2030" — the national plan the
 *  2030 targets belong to. Both target the same YEAR (VISION_YEAR), so only the
 *  name differs; never write either label as a literal. */
export const VISION = TENANT_PROFILE.visionPlan.label;
export const VISION_YEAR = TENANT_PROFILE.visionPlan.targetYear;
/** The focus country's ISO3 — "SAU" · "WON". */
export const COUNTRY_ISO3 = TENANT_PROFILE.iso3;
/** "Saudi Tourism Authority" · "Wonderland Tourism Authority" — who the dashboard
 *  is built for, and the acronym the shell signs itself with. */
export const AUTHORITY = TENANT_PROFILE.tourismAuthority.name;
export const AUTHORITY_SHORT = TENANT_PROFILE.tourismAuthority.short;
/** "Saudi Tourism Demand Sensing" — what the product is called. */
export const APP_NAME = `${TENANT_PROFILE.adjective} Tourism Demand Sensing`;
/** The same name as a two-line lockup, so a narrow wordmark slot never clips it. */
export const APP_NAME_LINES: readonly [string, string] = [`${TENANT_PROFILE.adjective} Tourism`, "Demand Sensing"];
/**
 * The traveler archetype's display name on THIS deployment.
 *
 * The seven/eight archetypes are the client's own segmentation research, named
 * in their own decks, and the demo published every one of them verbatim. Same
 * rule as the country literals above: a hardcoded "Drifters" or "Elites" in a
 * component is a bug on the demo. The replacement map lives in `@ow/shared`
 * because the server relabels the same names in the Product Matching 1.3
 * payload, and the two must not disagree about what a segment is called.
 *
 * `id` is the pipeline's segment id where the caller has one; `fallback` is
 * whatever it would otherwise have shown. Ids are never rewritten — they are
 * join keys.
 */
export const segmentLabel = (id: string, fallback = id): string => demoSegmentLabel(TENANT, id, fallback);
/**
 * How to cite the focus country's OWN documents in a "Source:" line.
 *
 * "TQ MT Demand Model", "Saudi MoT TIC open data" and "TQ Air connectivity
 * workbook" were literals in the section components, so the demo published the
 * client's ministry under a fabricated country's numbers — the single most
 * visible tenant leak on the deployed demo. Every source line naming this
 * country's government must read from here.
 *
 * Vendors (Euromonitor, UNWTO, Aviation Edge, Google Trends, Passport Index)
 * stay written out in the copy: they are the same real vendor on both tenants.
 */
export const SOURCE_DEMAND_MODEL = TENANT_PROFILE.sources.demandModel;
export const SOURCE_TOURISM_STATS = TENANT_PROFILE.sources.tourismStats;
export const SOURCE_CONNECTIVITY_WORKBOOK = TENANT_PROFILE.sources.connectivityWorkbook;
export const SOURCE_MINISTRY = TENANT_PROFILE.sources.ministry;
export const MINISTRY_SHORT = TENANT_PROFILE.sources.ministryShort;
/** True on the client deployment. Use ONLY to gate copy that documents a
 *  real-world provenance quirk of the client's own data collection — such a
 *  sentence is not merely mis-worded on the demo, it is untrue. Never use it to
 *  branch on which country to name; that is what the fields above are for. */
export const IS_CLIENT_TENANT = TENANT === DEFAULT_TENANT;

/**
 * Does the focus country have a religious travel segment the analysis models?
 *
 * Read this — never `IS_CLIENT_TENANT` — wherever copy says "non-religious",
 * "excl. religious" or names a religious purpose. On a country with no such
 * segment those phrases describe a subtraction of zero, and the client asked
 * for the subject to be absent from the demo rather than reported as small.
 */
export const HAS_RELIGIOUS_SEGMENT = TENANT_PROFILE.religiousSegment;

/** How the focus country is labelled in the supply comparison, where Saudi's
 *  religious segment is held out to keep the peer comparison like-for-like.
 *  A country with no religious segment needs no qualifier. */
export const FOCUS_EXCL_RELIGIOUS = HAS_RELIGIOUS_SEGMENT ? `${COUNTRY} (excl. religious)` : COUNTRY;

// --- Prioritization copy --------------------------------------------------
//
// The gravity model sizes opportunity as `feasible reach − what the country
// already captures`. On Saudi the second term is arrivals NET OF the religious
// strip, and every label says so. Where there is no religious segment the strip
// is empty, the subtraction is a no-op, and the same labels would be describing
// a distinction that does not exist. These three are the exact strings that
// differ; the arithmetic behind them does not.

/** "non-religious " qualifier, with its trailing space, or "" — interpolate
 *  directly before the noun: `${CURRENT_ARRIVALS_ADJ}visitors`. */
export const CURRENT_ARRIVALS_ADJ = HAS_RELIGIOUS_SEGMENT ? "non-religious " : "";
/** The term as it appears in the published formula. */
export const CURRENT_ARRIVALS_TERM = HAS_RELIGIOUS_SEGMENT ? "current_non_religious" : "current_arrivals";
/** Column label for the market's current share of its own outbound. */
export const CURRENT_SHARE_LABEL = HAS_RELIGIOUS_SEGMENT ? "Current non-relig share" : "Current share";

/**
 * Does a snippet of someone's own post text actually reference the focus
 * country?
 *
 * The Segmentation deep-dive publishes a member's verbatim quotes, but only the
 * ones that are about this country — a traveler's other exemplar quotes are
 * about the rest of their travel and would be quoted under a heading they have
 * nothing to do with. That filter used to be a hardcoded Saudi regex
 * (`SAUDI_TERMS` in deep-dive.tsx), which on the demo matched nothing and left
 * the panel permanently empty.
 *
 * The vocabulary is built FROM THE TENANT PROFILE — country name, capital,
 * provinces, gateway cities — plus the per-tenant extras below for the words a
 * profile has no field for. Provinces and gateways are the useful part: people
 * write "Baie Claire", not "Wonderland", exactly as they write "Jeddah", not "Saudi
 * Arabia".
 */
const FOCUS_TERM_EXTRAS: Record<string, readonly string[]> = {
  // Spellings, transliterations and landmarks a Saudi post uses that the
  // profile does not carry as a field.
  saudi: [
    "saudi",
    "ksa",
    "jiddah",
    "mecca",
    "makkah",
    "madinah",
    "alula",
    "al ula",
    "neom",
    "red sea",
    "umrah",
    "hajj",
    "khobar",
    "diriyah",
  ],
  // Wonderland landmarks that are not province or gateway names. Kept in step with
  // the seeded supply catalogue (seed/wonderland/supply.ts), which is what the
  // corpus's quotes actually name.
  wonderland: [
    "wonderland",
    "eastern sea",
    "standing stones",
    "palmiste bay",
    "coral shallows",
    "turtle point",
    "grand river",
    "sainte-lucie",
  ],
};

/**
 * Exported so BOTH tenants' vocabularies can be tested.
 *
 * The module-level `TENANT` resolves to the client under Vitest (there is no
 * `window`), so a test of `mentionsFocusCountry` alone can only ever exercise
 * one branch — and the demo's is the one that was silently broken before this
 * existed.
 */
export function buildFocusTerms(profile: TenantProfile): RegExp {
  const words = new Set<string>();
  const add = (term: string | undefined) => {
    const t = (term ?? "").trim().toLowerCase();
    if (t.length > 2) words.add(t);
  };
  add(profile.name);
  add(profile.shortName);
  add(profile.adjective);
  add(profile.capital);
  for (const region of profile.regions) {
    add(region.name);
    add(region.short);
  }
  for (const gateway of profile.gateways) add(gateway.name);
  for (const extra of FOCUS_TERM_EXTRAS[profile.id] ?? []) add(extra);
  // Longest first so "al bahah" is tried before "bahah" would be — the
  // alternation is ordered and a shorter prefix would shadow the longer term.
  const escaped = [...words]
    .sort((a, b) => b.length - a.length)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/[\s-]+/g, "[\\s-]?"));
  return new RegExp(`\\b(${escaped.join("|")})\\b`, "i");
}

const FOCUS_TERMS: RegExp = buildFocusTerms(TENANT_PROFILE);

/** True when a verbatim actually names the focus country or somewhere in it. */
export function mentionsFocusCountry(text: string): boolean {
  return FOCUS_TERMS.test(text);
}

/** Trailing " Authority · <host>." clause for the visa map's source line. */
export const VISA_AUTHORITY = ` Authority · ${TENANT_PROFILE.visaAuthority}.`;
/** The national carrier, named in connectivity copy. */
export const FLAG_CARRIER = TENANT_PROFILE.flagCarrier;
/** Source markets that reach the focus country overland — held out of
 *  Prioritization's matrix by default. Empty on a country with no land border,
 *  where the toggle does not render at all. */
export const LAND_BORDER_MARKETS: ReadonlySet<string> = new Set(TENANT_PROFILE.landBorderNeighbours);
/** "AlUla" · "Baie Claire" — the marquee destination a demo screen names. */
export const HERO_DESTINATION = TENANT_PROFILE.heroDestination;
/** Provinces, in tile-grid order. Replaces the old `SA_REGIONS` literal. */
export const REGIONS = TENANT_PROFILE.regions;
/** International gateways. Replaces the old `SAUDI_GATEWAYS` literal. */
export const GATEWAYS = TENANT_PROFILE.gateways;

/**
 * Re-key a Saudi-shaped illustrative table onto the current tenant's regions.
 *
 * The mock fallbacks the sections render while a query is in flight (or after
 * it fails) are keyed by Saudi province id. On the demo those keys match
 * nothing, so every province would read 0 — which looks like missing data
 * rather than a loading state. Mapping by POSITION instead keeps the
 * distribution shape (a couple of large provinces, a long tail) against
 * whatever ids the tenant actually uses.
 *
 * On the client tenant the region order is unchanged, so this is the identity.
 */
export function byRegionPosition<T>(saudiKeyed: Record<string, T>, saudiOrder: readonly string[]): Record<string, T> {
  const out: Record<string, T> = {};
  REGIONS.forEach((region, i) => {
    const sourceKey = saudiOrder[i % saudiOrder.length];
    const value = sourceKey === undefined ? undefined : saudiKeyed[sourceKey];
    if (value !== undefined) out[region.id] = value;
  });
  return out;
}
